class RelatedProductsBySku extends HTMLElement {
  constructor() {
    super();
    this.sectionId = this.dataset.sectionId;
    this.cardSectionId = this.dataset.cardSectionId;
    this.limit = parseInt(this.dataset.limit, 10) || 4;
    this.currentVariantId = this.dataset.currentVariantId;
    this.debug = this.hasAttribute('data-debug')
      || (typeof window !== 'undefined' && window.location && /[?&]rps_debug=1\b/.test(window.location.search));

    this.variantSkus = {};
    const json = this.querySelector('script[data-variant-skus]');
    if (json) {
      try {
        this.variantSkus = JSON.parse(json.textContent);
      } catch (e) {
        this.variantSkus = {};
      }
    }

    this.skuMode = this.querySelector('[data-related-section-wrapper]');
    this.grid = this.querySelector('[data-related-grid]');
    this.fallbackWrapper = this.querySelector('[data-related-fallback-wrapper]');

    this.loadedKey = null;
    this.productJsCache = new Map();
    this.handleBySkuCache = new Map();
  }

  connectedCallback() {
    this.boundVariantChange = this.onVariantChange.bind(this);
    document.addEventListener('variant:change', this.boundVariantChange);
    this.enhanceSliders();

    if (this.debug) {
      this.log('init', {
        sectionId: this.sectionId,
        currentVariantId: this.currentVariantId,
        variantSkus: this.variantSkus
      });
      window.relatedProductsBySku = this;
    }

    new IntersectionObserver(this.handleIntersection.bind(this), {
      rootMargin: '0px 0px 600px 0px'
    }).observe(this);
  }

  disconnectedCallback() {
    document.removeEventListener('variant:change', this.boundVariantChange);
  }

  // Arrows + mouse drag-to-scroll + click-vs-drag suppression for each .cw-rel-slider.
  // Handlers are bound once on the <ul> (survive innerHTML card injection); re-calling
  // just refreshes arrow enabled/disabled state (e.g. after cards are injected).
  enhanceSliders() {
    this.querySelectorAll('.cw-rel-slider').forEach((gutter) => {
      const grid = gutter.querySelector('.product-grid');
      if (!grid) return;
      if (gutter._cwUpdate) { gutter._cwUpdate(); return; }

      const prev = gutter.querySelector('.slider-button--prev');
      const next = gutter.querySelector('.slider-button--next');

      const step = () => {
        const item = grid.querySelector('.grid__item');
        const w = item ? item.getBoundingClientRect().width : 250;
        const gap = parseFloat(getComputedStyle(grid).columnGap || '0') || 0;
        return w + gap;
      };
      const update = () => {
        const itemCount = grid.querySelectorAll('.grid__item').length;
        const tooFew = itemCount <= 4;
        const max = grid.scrollWidth - grid.clientWidth - 1;
        const noScroll = tooFew || grid.scrollWidth <= grid.clientWidth + 2;
        if (prev) prev.toggleAttribute('disabled', noScroll || grid.scrollLeft <= 1);
        if (next) next.toggleAttribute('disabled', noScroll || grid.scrollLeft >= max);
        gutter.classList.toggle('cw-rel-slider--no-scroll', noScroll);
      };
      gutter._cwUpdate = update;

      prev && prev.addEventListener('click', () => grid.scrollBy({ left: -step() * 2, behavior: 'smooth' }));
      next && next.addEventListener('click', () => grid.scrollBy({ left: step() * 2, behavior: 'smooth' }));
      grid.addEventListener('scroll', update, { passive: true });
      window.addEventListener('resize', update);

      // Mouse drag-to-scroll (touch uses native scrolling). Suppress the click after a drag.
      // Prevent native image/link dragging so the drag scrolls instead of ghost-dragging.
      let down = false, startX = 0, startScroll = 0, moved = false;
      grid.addEventListener('dragstart', (e) => e.preventDefault());
      grid.addEventListener('pointerdown', (e) => {
        if (e.pointerType !== 'mouse' || e.button !== 0) return;
        down = true; moved = false; startX = e.clientX; startScroll = grid.scrollLeft;
      });
      grid.addEventListener('pointermove', (e) => {
        if (!down) return;
        const dx = e.clientX - startX;
        if (Math.abs(dx) > 5) { moved = true; grid.classList.add('cw-dragging'); }
        if (moved) grid.scrollLeft = startScroll - dx;
      });
      const end = () => { down = false; grid.classList.remove('cw-dragging'); };
      grid.addEventListener('pointerup', end);
      grid.addEventListener('pointerleave', end);
      grid.addEventListener('pointercancel', end);
      grid.addEventListener('click', (e) => {
        if (moved) { e.preventDefault(); e.stopPropagation(); moved = false; }
      }, true);

      update();
    });
  }

  handleIntersection(entries, observer) {
    if (!entries[0].isIntersecting) return;
    observer.unobserve(this);
    this.refresh();
  }

  onVariantChange(event) {
    const variant = event.detail && event.detail.variant;
    if (!variant) return;
    this.currentVariantId = String(variant.id);
    this.refresh();
  }

  getCurrentSkus() {
    const skus = this.variantSkus[this.currentVariantId];
    if (!Array.isArray(skus)) return [];
    return skus
      .map((s) => (typeof s === 'string' ? s.trim() : ''))
      .filter(Boolean)
      .slice(0, this.limit);
  }

  async refresh() {
    const skus = this.getCurrentSkus();
    this.log('refresh', { variantId: this.currentVariantId, skus });

    if (skus.length === 0) {
      this.showFallback();
      return;
    }

    const key = skus.join('|');
    this.showSkuMode();

    if (this.loadedKey === key) return;

    this.grid.setAttribute('aria-busy', 'true');

    try {
      const resolved = await this.resolveSkusToHandles(skus);
      const handles = resolved.map((r) => r.handle).filter(Boolean);

      this.log('resolved', resolved);

      if (!handles.length) {
        this.warn('No SKUs resolved to a product. Falling back.', { skus });
        this.showFallback();
        return;
      }

      const cards = await Promise.all(handles.map((h) => this.fetchCardHtml(h)));
      const html = cards.filter(Boolean).join('');
      if (!html) {
        this.warn('All card fetches returned empty. Falling back.', { handles });
        this.showFallback();
        return;
      }
      this.grid.innerHTML = html;
      this.loadedKey = key;
      this.enhanceSliders();
    } catch (e) {
      console.error('[related-products-by-sku]', e);
      this.showFallback();
    } finally {
      this.grid.removeAttribute('aria-busy');
    }
  }

  showSkuMode() {
    if (this.skuMode) this.skuMode.hidden = false;
    if (this.fallbackWrapper) this.fallbackWrapper.hidden = true;
  }

  showFallback() {
    if (this.skuMode) this.skuMode.hidden = true;
    if (this.fallbackWrapper) this.fallbackWrapper.hidden = false;
  }

  async resolveSkusToHandles(skus) {
    const currentHandle = this.getCurrentProductHandle();
    const seen = new Set();
    if (currentHandle) seen.add(currentHandle);

    const results = await Promise.all(skus.map((sku) => this.findHandleBySku(sku)));

    const out = [];
    for (let i = 0; i < skus.length; i++) {
      const handle = results[i];
      const entry = { sku: skus[i], handle };
      if (handle && !seen.has(handle)) {
        seen.add(handle);
        out.push(entry);
      } else if (!handle) {
        out.push(entry);
      }
    }
    return out.slice(0, this.limit);
  }

  getCurrentProductHandle() {
    const m = window.location.pathname.match(/\/products\/([^\/?#]+)/);
    return m ? m[1] : null;
  }

  async findHandleBySku(sku) {
    if (!sku) return null;
    if (this.handleBySkuCache.has(sku)) return this.handleBySkuCache.get(sku);

    const candidates = await this.fetchCandidateHandles(sku);
    this.log('candidates', { sku, candidates });

    for (const handle of candidates) {
      const ok = await this.productHasSku(handle, sku);
      if (ok) {
        this.handleBySkuCache.set(sku, handle);
        return handle;
      }
    }

    this.warn(`No exact SKU match found for "${sku}". Candidates tried: ${candidates.join(', ') || '(none)'}`);
    this.handleBySkuCache.set(sku, null);
    return null;
  }

  async fetchCandidateHandles(sku) {
    const handles = [];
    const seen = new Set();

    const params1 = new URLSearchParams();
    params1.set('q', sku);
    params1.set('resources[type]', 'product');
    params1.set('resources[limit]', '10');
    params1.set('resources[options][fields]', 'variants.sku');
    params1.set('resources[options][unavailable_products]', 'last');
    await this.collectHandlesFromSuggest(params1, handles, seen);

    if (handles.length === 0) {
      const params2 = new URLSearchParams();
      params2.set('q', sku);
      params2.set('resources[type]', 'product');
      params2.set('resources[limit]', '10');
      params2.set('resources[options][unavailable_products]', 'last');
      await this.collectHandlesFromSuggest(params2, handles, seen);
    }

    return handles;
  }

  async collectHandlesFromSuggest(params, handles, seen) {
    try {
      const res = await fetch(`/search/suggest.json?${params.toString()}`, {
        headers: { Accept: 'application/json' }
      });
      if (!res.ok) return;
      const data = await res.json();
      const products = (data && data.resources && data.resources.results && data.resources.results.products) || [];
      for (const p of products) {
        const handle = this.extractHandle(p.url);
        if (handle && !seen.has(handle)) {
          seen.add(handle);
          handles.push(handle);
        }
      }
    } catch (e) {
      this.warn('suggest fetch failed', e);
    }
  }

  extractHandle(url) {
    if (!url) return null;
    const m = String(url).match(/\/products\/([^\/?#]+)/);
    return m ? m[1] : null;
  }

  async productHasSku(handle, sku) {
    const data = await this.fetchProductJs(handle);
    if (!data) return false;
    const target = String(sku).trim();
    const variants = data.variants || [];
    return variants.some((v) => String(v.sku || '').trim() === target);
  }

  async fetchProductJs(handle) {
    if (this.productJsCache.has(handle)) return this.productJsCache.get(handle);
    try {
      const res = await fetch(`/products/${encodeURIComponent(handle)}.js`, {
        headers: { Accept: 'application/json' }
      });
      if (!res.ok) {
        this.productJsCache.set(handle, null);
        return null;
      }
      const data = await res.json();
      this.productJsCache.set(handle, data);
      return data;
    } catch (e) {
      this.warn(`product.js fetch failed for ${handle}`, e);
      this.productJsCache.set(handle, null);
      return null;
    }
  }

  async fetchCardHtml(handle) {
    const url = `/products/${encodeURIComponent(handle)}?section_id=${encodeURIComponent(this.cardSectionId)}`;
    try {
      const res = await fetch(url);
      if (!res.ok) {
        this.warn(`card fetch failed for ${handle}: HTTP ${res.status}`);
        return '';
      }
      const text = await res.text();
      const doc = new DOMParser().parseFromString(text, 'text/html');
      const li = doc.querySelector('li.grid__item');
      if (li) return li.outerHTML;
      this.warn(`card fetch returned no <li> for ${handle}`);
      return '';
    } catch (e) {
      this.warn(`card fetch error for ${handle}`, e);
      return '';
    }
  }

  log(label, payload) {
    if (!this.debug) return;
    if (payload !== undefined) {
      console.info(`[related-products-by-sku] ${label}`, payload);
    } else {
      console.info(`[related-products-by-sku] ${label}`);
    }
  }

  warn(message, payload) {
    if (payload !== undefined) {
      console.warn(`[related-products-by-sku] ${message}`, payload);
    } else {
      console.warn(`[related-products-by-sku] ${message}`);
    }
  }

  /**
   * Diagnostic helper — call from browser console after enabling debug:
   *   document.querySelector('related-products-by-sku').audit()
   * Returns per-SKU resolution status without re-rendering the grid.
   */
  async audit() {
    const skus = this.getCurrentSkus();
    const report = [];
    for (const sku of skus) {
      const candidates = await this.fetchCandidateHandles(sku);
      let matchedHandle = null;
      const verified = [];
      for (const handle of candidates) {
        const data = await this.fetchProductJs(handle);
        const variantSkus = (data && data.variants ? data.variants : []).map((v) => v.sku);
        const isMatch = variantSkus.some((s) => String(s || '').trim() === String(sku).trim());
        verified.push({ handle, variantSkus, isMatch });
        if (isMatch && !matchedHandle) matchedHandle = handle;
      }
      report.push({ sku, matchedHandle, candidates, verified });
    }
    console.table(report.map((r) => ({ sku: r.sku, matched: r.matchedHandle || '(none)', candidates: r.candidates.join(', ') })));
    return report;
  }
}

customElements.define('related-products-by-sku', RelatedProductsBySku);
